import { useEffect, useState, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import { env } from 'env';

import { checkMuteModalHidden } from '../../../helpers';

import { UseShowInitialMuteModalType } from './interfaces';

import { useConference } from '../../DashboardContent/Dolby';
import { useMytaverseEvent } from '../../../providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import {
  useClientSpecificCameraVisibility,
  useClientSpecificMicVisibility,
} from '@hooks/clientSpecificUtils';
import ParticipantScopes from '@constants/scopes';
import { ExperienceFeatures } from '@interfaces/event';
import { isEventFeatureEnabled } from '@helpers/events';

export const useShowInitialMuteModal: UseShowInitialMuteModalType = ({
  eventId,
  initialMuteModalData,
}) => {
  const { openModal } = initialMuteModalData;

  useEffect(() => {
    const muteModalHidden = checkMuteModalHidden(eventId);

    if (!muteModalHidden) {
      openModal();
    }
  }, [eventId]);
};

export interface HomeBottomButtonsVisibility {
  showWebcamButton: boolean;
  showMicButton: boolean;
  showScreenShareButton: boolean;
  showRaiseHandButton: boolean;
  showToggleViewButton: boolean;
  showTrainerTools: boolean;
  hasAnyButton: boolean;
}

// Single source of truth for which buttons the bottom-left bar renders.
// Used both by HomeBottomButtonOptions (to render the buttons) and by
// HomeBottomBar (to decide whether to render the bar container at all, so an
// empty bar does not show up as a small circle — MV-1367).
export const useHomeBottomButtonsVisibility =
  (): HomeBottomButtonsVisibility => {
    const { REACT_APP_CLASSROOM_EVENT_TAGS } = env;
    const CLASSROOM_TAGS = (REACT_APP_CLASSROOM_EVENT_TAGS || '')
      .split(',')
      .map((t) => t.toLowerCase().trim());

    const { isCameraEnabled } = useClientSpecificCameraVisibility();
    const { isMicEnabled } = useClientSpecificMicVisibility();

    const { ueScreenScreenName } = useConference();
    const { userPermissions } = useMytaverse();
    const {
      currentEvent,
      canSpeak,
      canShowCamera,
      canShareCamera,
      canShareScreen,
      currentParticipant,
    } = useMytaverseEvent();

    const isTrainer = userPermissions?.includes(
      ParticipantScopes.HubTrainingToolHubEvent,
    );

    const eventTags = currentEvent?.eventTags ?? [];
    const isClassroomEvent = eventTags.some(
      (tag) =>
        typeof tag === 'string' && CLASSROOM_TAGS.includes(tag.toLowerCase()),
    );

    const isRaiseHandEnabled = isEventFeatureEnabled(
      currentEvent,
      ExperienceFeatures.RaiseHand,
    );

    const showTrainerTools = isClassroomEvent && !!isTrainer;
    const showRaiseHandButton =
      isClassroomEvent && !isTrainer && isRaiseHandEnabled;

    const isSeatRegion = currentParticipant?.isSeatRegion;

    const isToggleViewEnabled = isEventFeatureEnabled(
      currentEvent,
      ExperienceFeatures.ToggleView,
    );

    const showWebcamButton =
      canSpeak &&
      isCameraEnabled &&
      (canShowCamera || canShareCamera) &&
      !isSeatRegion;

    const showMicButton = canSpeak && isMicEnabled;

    const showScreenShareButton =
      canSpeak &&
      canShareScreen &&
      !isMobile &&
      !!ueScreenScreenName &&
      !showTrainerTools;

    const showToggleViewButton = isToggleViewEnabled;

    const hasAnyButton =
      showWebcamButton ||
      showMicButton ||
      showScreenShareButton ||
      showRaiseHandButton ||
      showToggleViewButton ||
      showTrainerTools;

    return {
      showWebcamButton,
      showMicButton,
      showScreenShareButton,
      showRaiseHandButton,
      showToggleViewButton,
      showTrainerTools,
      hasAnyButton,
    };
  };

export const useOpenSettingsModal = () => {
  const [open, setOpen] = useState(false);

  const switchOpenModal = () => setOpen((open) => !open);

  const closeModal = useCallback(() => setOpen(false), []);

  return {
    open,
    switchOpenModal,
    closeModal,
  };
};
